<template>
	<section class="container-fluid next-case">
		<div class="row">
			<div class="col-xs-11 col-xs-offset-1 col-sm-12 col-sm-offset-0 col-md-10 col-md-offset-2 col-lg-8 col-lg-offset-4 col-xxl-7 col-xxl-offset-5">
				<NuxtLink v-if="nextWork.featured_image"
						  :to="`/work/${nextWork.slug}`"
						  class="next-case-wrapper"
						  :style="{ '--background-color': nextWork.backgroundColor, '--foreground-color': nextWork.foregroundColor, '--details-color': nextWork.detailsColor }"
						  data-cursor-custom
						  data-smiley
						  @mouseenter="mouseEnter"
						  @mouseleave="mouseLeave">
					<div class="image-container">
						<div ref="mediaWrapper"
							 class="image-wrapper">
							<clientOnly>
								<baseVideo v-if="nextWork.thumbnail_video && !isMobile"
										   :data="nextWork.thumbnail_video"
										   preload
										   loop
										   autoplay
										   muted
										   playsinline />
							</clientOnly>

							<basePicture v-if="nextWork.thumbnail_image || nextWork.featured_image"
										 :image="nextWork.thumbnail_image || nextWork.featured_image"
										 sizes="xs:30vw md:30vw lg:30vw xl:30vw 2xl:30vw 3xl:30vw"
										 class="static-image"
										 :class="{ 'hidden': hover && !isMobile }" />
						</div>
					</div>

					<div class="content">
						<p class="next-project-text">
							Case: <span class="bolder">{{ nextWork.title }}</span>
						</p>
						<div ref="title"
							 class="title-wrapper">
							<h5 class="display-title so-expand"
								v-html="nextWork.display_title || nextWork.title" />
						</div>
						<div class="arrow">
							<div class="line" />
							<arrowTip class="point" />
						</div>
					</div>
				</NuxtLink>
			</div>
		</div>
	</section>
</template>

<script setup>
import arrowTip from '~/assets/images/icon-caret-right.svg'

const props = defineProps({
	currentCaseSlug: {
		type: String,
		required: true
	}
})

const { directus, readItems } = useDirectus()

const isMobile = useBreakpoint('below', 'md')
const hover = ref(false)
const mediaWrapperRef = useTemplateRef('mediaWrapper')

// guards the async play() below — the pointer may have left before it resolves
let isHovering = false

function videoEl() {
	return mediaWrapperRef.value?.querySelector('video') || null
}

// Safari won't autoplay at all in Low Power Mode, and holds a muted video until WebKit
// considers it visible — so a card can sit paused on a black frame. play() stays
// rejected until the page has had a user gesture, hence the swallowed catch.
function ensurePlaying() {
	const video = videoEl()

	if (!video || !video.paused) return

	video.play().catch(() => { })
}

function mouseEnter() {
	if (isMobile.value) return

	isHovering = true

	const video = videoEl()

	// nothing to reveal, or it's already running — behave as before
	if (!video || !video.paused) {
		hover.value = true
		return
	}

	// only uncover the video once it is genuinely playing, otherwise the static image
	// fades away to a black frame and a play icon for anyone in Low Power Mode
	video.play()
		.then(() => isHovering && (hover.value = true))
		.catch(() => { })
}

function mouseLeave() {
	isHovering = false
	hover.value = false
}

onMounted(() => {
	// baseVideo sits inside clientOnly, so it isn't in the DOM yet on this tick
	nextTick(() => {
		const video = videoEl()

		if (!video) return

		video.addEventListener('canplay', ensurePlaying)
		ensurePlaying()
	})
})

onBeforeUnmount(() => {
	videoEl()?.removeEventListener('canplay', ensurePlaying)
})

const { data: allWorks } = await useAsyncData('all-works-lite', () => {
	return directus.request(readItems('work', allWorksLiteQuery()))
})

/**
 * Computes the "next case" for navigation given a list of works and the current case slug.
 *
 * Exposed computed values:
 * - allWorksLength: number of items in allWorks (0 when undefined)
 * - startIndex: index to start searching from (the item immediately after currentCaseSlug).
 *     - Returns -1 when allWorks is missing/empty.
 *     - If currentCaseSlug is not found, search starts at index 0.
 * - nextWorkSlug: slug of the next work to navigate to.
 *     - Searches from startIndex and wraps around, returning the first work that has a featured_image.
 *     - If no work has a featured_image, falls back to the slug at startIndex.
 *     - Returns null when there are no works or startIndex is -1.
 * - nextWork: the work object that matches nextWorkSlug, or null if none.
 *
 * Key changes / behavior notes:
 * - The search now begins at the element immediately after the current case (wraps to the beginning).
 * - Works without a featured_image are skipped when selecting the "next" work, improving UX for image-driven navigation.
 * - A clear fallback is provided: if no item with a featured_image is found, the code returns the slug at startIndex.
 * - Uses a sentinel value (-1) to indicate an invalid startIndex so downstream logic can return nulls consistently.
 *
 * Edge cases:
 * - Empty or undefined allWorks => length 0, startIndex -1, nextWorkSlug and nextWork are null.
 * - currentCaseSlug not found => startIndex becomes 0 (start from the first item).
 * - Single-item lists and full wrap-around are handled correctly.
 */

const allWorksLength = computed(() => allWorks.value?.length ?? 0)

const startIndex = computed(() => {
	if (!allWorks.value || allWorksLength.value === 0) return -1

	const idx = allWorks.value.findIndex(work => work.slug === props.currentCaseSlug)

	return (idx === -1) ? 0 : (idx + 1) % allWorksLength.value
})

const nextWorkSlug = computed(() => {
	if (!allWorks.value || allWorksLength.value === 0 || startIndex.value === -1) return null

	let idx = startIndex.value
	for (let i = 0; i < allWorksLength.value; i++) {
		const work = allWorks.value[idx]
		// if (work && work.featured_image && work.thumbnail_video) return work.slug
		if (work && work.featured_image) return work.slug
		idx = (idx + 1) % allWorksLength.value
	}

	// fallback to the start index if no work has featured_image
	return allWorks.value[startIndex.value]?.slug ?? null
})

const nextWork = computed(() => {
	if (!allWorks.value || !nextWorkSlug.value) return null

	return allWorks.value.find(work => work.slug === nextWorkSlug.value) ?? null
})
</script>

<style lang="scss" scoped>
.next-case {
	@include standard-top-bottom-margin;

	&-wrapper {
		position: relative;
		display: flex;
		flex-flow: column nowrap;
		justify-content: space-between;
		align-items: stretch;
		gap: rem-calc(25);

		@include for-devices-above(sm) {
			display: flex;
			flex-flow: row nowrap;
			border: unset;
			gap: $gutter-width;
		}

		&:hover {
			.arrow {
				width: calc(100% - 7px);
			}
		}

		.image-container {
			max-width: 40%;
			display: flex;
			flex-flow: column nowrap;


			.image-wrapper {
				position: relative;
				overflow: hidden;
				aspect-ratio: 16 / 10;
				@include rounded-corners;

				video {
					position: absolute;
					top: 50%;
					left: 50%;
					transform: translate(-50%, -50%);
					object-fit: cover;
					width: 100%;
					height: 100%;
					z-index: 1;
				}

				:deep(picture) {
					position: relative;
					display: flex;
					z-index: 10;
					transition: opacity 0.5s $easeOutQuint;
					will-change: opacity;

					img {
						display: block;
						object-fit: cover;
					}

					&.hidden {
						opacity: 0;
					}
				}
			}
		}

		.content {
			display: flex;
			flex-flow: column nowrap;
			justify-content: space-around;
			width: 100%;
			// align-self: space-around;

			@include for-devices-above(sm) {
				width: 60%;
			}

			@include for-devices-above(md) {
				width: 55.556%;
			}

			@include for-devices-above(lg) {
				width: 62.5%;
			}

			@include for-devices-above(xxl) {
				width: 70%;
			}

			.next-project-text {
				color: var(--details-color, $black);
				margin-bottom: rem-calc(13);

				@include for-devices-above(md) {
					margin-bottom: rem-calc(15);
				}
			}

			.title-wrapper {
				margin-bottom: rem-calc(16);

				@include for-devices-above(md) {
					margin-bottom: rem-calc(21);
				}

				.display-title {
					margin: 0;
				}
			}

			.arrow {
				.line {
					background-color: var(--details-color, $black);
				}

				.point {
					color: var(--details-color, $black);
				}
			}
		}
	}
}
</style>
